import { HTMLAttributes, useContext, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { gql, useQuery } from '@apollo/client';
import { Progress, Table } from 'antd';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { up } from 'styled-breakpoints';
import styled, { useTheme } from 'styled-components';
import { match } from 'ts-pattern';
import {
  MetricPreviousPeriodValue,
  MetricSumNewContactsAcquisitionGroupByType,
  MetricSumNewContactsAcquisitionGroupedItem,
  Query,
  QueryMetricSumNewContactsAcquisitionGroupedArgs,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { whenDefined } from '@lgg/isomorphic/when-defined';
import { TableColumns } from 'src/components/general/display/table';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import { DashboardFiltersContext } from 'src/components/pages/dashboard/components/filters/dashboard-filters-context';
import {
  Card,
  CardBaseContent,
  ColoredDotLabel,
  emptyValuePlaceholder,
  getChartItemColorByIndex,
  SimpleDashboardTable,
  CompareBadge,
  CardMainNumericValue,
  useCardValueHelpers,
  DashboardTableColumnTitle,
  DashboardTableItemValue,
  DashboardTableCategoryPercentLabel,
  getDashboardPercentageDisplayValue,
  DashboardTableEmptyValue,
  dashboardTableBodyOVerlayStyles,
  ChipFilters,
} from 'src/components/pages/dashboard/components/shared';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';

export const GET_METRIC_NEW_CONTACT_ACQUISITION_GROUPED_QUERY = gql`
  query GetMetricSumNewContactsAcquisitionGrouped(
    $institutionId: Int!
    $period: MetricPeriodWhereInput!
    $comparePeriod: MetricComparePeriodInput
    $where: MetricSumNewContactsAcquisitionGroupedWhereInput
    $groupBy: MetricSumNewContactsAcquisitionGroupByType!
  ) {
    metricSumNewContactsAcquisitionGrouped(
      institutionId: $institutionId
      period: $period
      comparePeriod: $comparePeriod
      where: $where
      groupBy: $groupBy
    ) {
      total
      items {
        label
        total
        previousPeriod {
          total
          trendChangePercentage
          trendChangeIndicator
        }
      }
      untrackedItem {
        label
        total
        previousPeriod {
          total
          trendChangePercentage
          trendChangeIndicator
        }
      }
      previousPeriod {
        total
        trendChangePercentage
        trendChangeIndicator
      }
      lastUpdatedAt
    }
  }
`;

const StyledTable = styled(SimpleDashboardTable)`
  .ant-table-wrapper {
    height: 100%;
  }

  .ant-table.ant-table-small {
    height: 100%;
  }

  .ant-table.ant-table-small .ant-table-tbody > tr > td,
  .ant-table.ant-table-small .ant-table-thead > tr > th {
    line-height: 14px;
    padding: 11px 9.5px;

    ${up('xl')} {
      padding: 10px 17px;

      &:first-child {
        padding-right: 32.5px;
      }
    }
  }

  .ant-table.ant-table-small .ant-table-thead > tr > th {
    font-size: 10px;
    line-height: 10px;
    letter-spacing: -0.1px;

    ${up('xl')} {
      padding-bottom: 8px;
    }
  }

  .ant-table.ant-table-small .ant-table-tbody {
    ${dashboardTableBodyOVerlayStyles};

    & > tr:last-child > td {
      padding-bottom: 35px;
    }
  }
` as typeof Table;

const ChartInfoContainer = styled(FlexColumn)`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  position: absolute;
  width: 100%;
`;

const ChartInfoValue = styled.span`
  text-align: center;
`;

const ChartInfoValueLabel = styled.span`
  border-bottom: 1px solid ${({ theme }) => theme.colors.koala};
  color: ${({ theme }) => theme.colors.flint};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 13px;
  letter-spacing: -0.39px;
  line-height: 16px;
  margin: 0 6px 2px;
  text-align: center;
`;

const ChartInfoPreviousPeriod = styled.span`
  color: ${({ theme }) => theme.colors.flint};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 10px;
  font-style: normal;
  letter-spacing: -0.1px;
  line-height: 13px;
  margin-top: 7px;
  text-align: center;

  strong {
    color: ${({ theme }) => theme.colors.flint};
  }
`;

const ChartWrapperContainer = styled.div`
  height: 100%;
  position: relative;
  width: 100%;
`;

const StyledResponsiveContainer = styled(ResponsiveContainer)`
  pointer-events: none;
`;

const ChartInfoPreviousValue = styled.span`
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 10px;
  line-height: 13px;
  letter-spacing: -0.1px;
`;

type ChartOverviewInformationProps = {
  value: number | null;
  previousValue?: number;
  children: React.ReactElement;
};

const ChartOverviewInformation = ({
  value,
  children,
  previousValue,
}: ChartOverviewInformationProps) => {
  const { t } = useTranslation(['dashboard']);
  const { numericCardValueTransformer } = useCardValueHelpers();

  if (value === null) {
    return null;
  }

  return (
    <ChartWrapperContainer data-lgg-id="chart-overview-information">
      <ChartInfoContainer>
        <ChartInfoValue data-lgg-id="chart-overview-information-value">
          <CardMainNumericValue value={value} />
        </ChartInfoValue>
        <ChartInfoValueLabel>
          {t('dashboard:cards.newContacts.chart.totalContacts')}
        </ChartInfoValueLabel>
        <ChartInfoPreviousPeriod data-lgg-id="chart-overview-information-previous-period">
          <Trans
            i18nKey="dashboard:cards.newContacts.chart.previousPeriod"
            components={{
              strong: <ChartInfoPreviousValue />,
            }}
            values={{
              value:
                previousValue !== undefined
                  ? numericCardValueTransformer(previousValue)
                  : emptyValuePlaceholder,
            }}
          />
        </ChartInfoPreviousPeriod>
      </ChartInfoContainer>
      {children}
    </ChartWrapperContainer>
  );
};

const ChartContainer = styled.div`
  height: 226px;
  margin-bottom: 40px;
  width: 226px;

  ${up('md')} {
    height: 186px;
    margin-bottom: 10px;
    width: 186px;
  }

  ${up('xl')} {
    height: 226px;
    margin-right: 40px;
    width: 226px;
  }
`;

const DetailsWrapper = styled.div`
  width: 100%;

  .ant-table-wrapper {
    &,
    .ant-spin-nested-loading,
    .ant-spin-container,
    .ant-table-container {
      height: 100%;
    }
  }

  ${up('xl')} {
    height: 100%;
    width: calc(100% - 266px);
  }
`;

const CardDataContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;

  ${up('xl')} {
    align-items: flex-start;
    flex-direction: row;
    overflow: hidden;
  }
`;

const StyledProgress = styled(Progress)`
  line-height: 0;
  margin-top: 2px;
`;

const ProgressLabelTopContent = styled(FlexRow)`
  justify-content: space-between;
`;

const ProgressLabelLabel = styled.span`
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 12px;
  letter-spacing: -0.12px;
  line-height: 15px;
`;

const PreviousValueBadgeContainer = styled(FlexRow)`
  justify-content: flex-end;
`;

const TotalContactsContent = styled(FlexRow)`
  align-items: center;
  justify-content: flex-end;
`;

const CardContentContainer = styled(CardBaseContent)`
  height: 527px;
  padding: 20px 20px 0;

  ${up('md')} {
    height: 446px;
  }

  ${up('xl')} {
    height: 343px;
  }
`;

const EmptyCardContentContainer = styled(CardContentContainer)`
  padding: 0;
`;

const StyledChipFilters = styled(ChipFilters)`
  margin-bottom: 30px;

  ${up('md')} {
    margin-bottom: 20px;
  }
` as typeof ChipFilters;

export const NewContactsCard = () => {
  const [filterByValue, setFilterByValue] =
    useState<MetricSumNewContactsAcquisitionGroupByType>('CAMPAIGN');
  const currentInstitution = useCurrentInstitution();
  const handleGraphQLError = useHandleGraphQLError();
  const { t } = useTranslation(['common', 'dashboard']);
  const theme = useTheme();
  const { targetUserId, metricPeriodWhereInput, metricComparePeriodWhereInput } =
    useContext(DashboardFiltersContext);
  const breakpointUpXl = useBreakpoint(up('xl'));

  const newContactAcquisitionQueryResult = useQuery<
    Pick<Query, 'metricSumNewContactsAcquisitionGrouped'>,
    Partial<QueryMetricSumNewContactsAcquisitionGroupedArgs>
  >(GET_METRIC_NEW_CONTACT_ACQUISITION_GROUPED_QUERY, {
    variables: {
      institutionId: currentInstitution.id,
      period: metricPeriodWhereInput,
      comparePeriod: metricComparePeriodWhereInput,
      where: whenDefined(targetUserId, (id) => ({
        dimensionContactAssignee: { id: { _eq: id } },
      })),
      groupBy: filterByValue,
    },
    onError: handleGraphQLError,
  });

  const detailLabel = useMemo(() => {
    // eslint-disable-next-line custom-rules/require-try-catch-for-exhaustive
    const translationKey = match(filterByValue)
      .with('CAMPAIGN', () => 'common:campaign' as const)
      .with('SOURCE', () => 'common:source' as const)
      .with('CHANNEL', () => 'common:channel' as const)
      .exhaustive();

    return t(translationKey);
  }, [filterByValue, t]);

  const { data, loading } = newContactAcquisitionQueryResult;

  const newContactsAcquisitionData = data?.metricSumNewContactsAcquisitionGrouped;

  const {
    total = null,
    previousPeriod,
    lastUpdatedAt,
    items: newContactAcquisitionItems = [],
    untrackedItem,
  } = newContactsAcquisitionData ?? {};

  const columns: TableColumns<MetricSumNewContactsAcquisitionGroupedItem>[] = useMemo(
    () => [
      {
        key: 'label',
        title: (
          <DashboardTableColumnTitle data-lgg-id="table-detail-label">
            {detailLabel}
          </DashboardTableColumnTitle>
        ),
        dataIndex: 'label',
        render: (value, data, index) => {
          const { total: categoryTotal = null } = data;

          if (categoryTotal === null) {
            return null;
          }

          const itemColor =
            value === untrackedItem?.label
              ? theme.colors.casper
              : getChartItemColorByIndex(index);
          const percent = total
            ? parseFloat(((categoryTotal / total) * 100).toFixed(0))
            : 0;

          return breakpointUpXl ? (
            <FlexColumn>
              <ProgressLabelTopContent>
                <ProgressLabelLabel>{value}</ProgressLabelLabel>
              </ProgressLabelTopContent>
              <StyledProgress
                percent={percent}
                showInfo={false}
                strokeColor={itemColor}
              />
            </FlexColumn>
          ) : (
            <ColoredDotLabel color={itemColor} value={value} />
          );
        },
      },

      {
        key: 'total-contacts',
        title: (
          <DashboardTableColumnTitle>
            {t('dashboard:cards.newContacts.details.columns.totalContacts')}
          </DashboardTableColumnTitle>
        ),
        dataIndex: 'total',
        width: '109px',
        render: (value, data) => {
          const { total: categoryTotal = null } = data;

          if (categoryTotal === null) {
            return null;
          }

          return (
            <TotalContactsContent>
              <DashboardTableItemValue data-lgg-id="table-detail-total-contacts">
                {value}
              </DashboardTableItemValue>
              <DashboardTableCategoryPercentLabel data-lgg-id="table-detail-contacts-percentage">
                {getDashboardPercentageDisplayValue(total ?? 0, categoryTotal)}
              </DashboardTableCategoryPercentLabel>
            </TotalContactsContent>
          );
        },
      },
      {
        key: 'previous-period',
        title: (
          <DashboardTableColumnTitle>
            {t('dashboard:cards.newContacts.details.columns.previousPeriod')}
          </DashboardTableColumnTitle>
        ),
        dataIndex: 'previousPeriod',
        width: '79px',
        render: (value: MetricPreviousPeriodValue, data) => (
          <PreviousValueBadgeContainer>
            {data.previousPeriod ? (
              <CompareBadge
                trendChangePercentage={data.previousPeriod?.trendChangePercentage}
                trendChangeIndicator={data.previousPeriod?.trendChangeIndicator}
                variant="compact"
                fromValue={value?.total.toLocaleString()}
                toValue={data?.total?.toLocaleString()}
                testId="table-detail-previous-period"
                forceEmptyState={data.previousPeriod.total === 0}
              />
            ) : (
              <DashboardTableEmptyValue />
            )}
          </PreviousValueBadgeContainer>
        ),
      },
    ],
    [breakpointUpXl, detailLabel, t, total, untrackedItem, theme],
  );

  const filterOptions: {
    value: MetricSumNewContactsAcquisitionGroupByType;
    label: string;
    testId: string;
  }[] = useMemo(
    () => [
      {
        value: 'CAMPAIGN',
        label: t('dashboard:cards.newContacts.filterBy.byCampaign'),
        testId: 'dashboard-new-contacts-card-group-by-campaign-option',
      },
      {
        value: 'SOURCE',
        label: t('dashboard:cards.newContacts.filterBy.bySource'),
        testId: 'dashboard-new-contacts-card-group-by-source-option',
      },
      {
        value: 'CHANNEL',
        label: t('dashboard:cards.newContacts.filterBy.byChannel'),
        testId: 'dashboard-new-contacts-card-group-by-channel-option',
      },
    ],
    [t],
  );

  const hasRecords = newContactAcquisitionItems.length > 0 || untrackedItem !== null;
  const cardTitle = t('dashboard:cards.newContacts.title');

  return (
    <Card loading={loading} data-lgg-id="dashboard-new-contacts-card">
      <Card.Header
        title={cardTitle}
        cardInfoI18nKey="dashboard:cards.newContacts.tooltipDescription"
        updatedAt={lastUpdatedAt}
        cardTextTitle={cardTitle}
      />
      <Card.Content>
        {hasRecords ? (
          <CardContentContainer>
            <StyledChipFilters
              options={filterOptions}
              selectedOption={filterByValue}
              onChanged={setFilterByValue}
            />
            <CardDataContainer>
              <ChartContainer>
                <ChartOverviewInformation
                  previousValue={previousPeriod?.total}
                  value={total}
                >
                  <StyledResponsiveContainer>
                    <PieChart
                      margin={{
                        bottom: 0,
                        left: 0,
                        right: 0,
                        top: 0,
                      }}
                    >
                      <Pie
                        data={[
                          ...newContactAcquisitionItems,
                          ...(untrackedItem ? [untrackedItem] : []),
                        ]}
                        innerRadius="80%"
                        outerRadius="100%"
                        paddingAngle={1.5}
                        cornerRadius={5}
                        dataKey="total"
                        animationDuration={0}
                      >
                        {newContactAcquisitionItems.map((item, index) => (
                          <Cell
                            key={`cell-${item.label}-${index}`}
                            fill={getChartItemColorByIndex(index)}
                          />
                        ))}
                        {untrackedItem && (
                          <Cell
                            key={`cell-untracked-${untrackedItem.label}`}
                            fill={theme.colors.casper}
                          />
                        )}
                      </Pie>
                    </PieChart>
                  </StyledResponsiveContainer>
                </ChartOverviewInformation>
              </ChartContainer>
              <DetailsWrapper>
                <StyledTable
                  data-lgg-id="new-contacts-card-details-table"
                  showSorterTooltip={false}
                  loading={loading}
                  columns={columns}
                  dataSource={[
                    ...newContactAcquisitionItems,
                    ...(untrackedItem ? [untrackedItem] : []),
                  ]}
                  pagination={false}
                  tableLayout="auto"
                  size="small"
                  rowKey={({ label }) => `new-contacts-card-details-table-${label}`}
                  scroll={{ y: breakpointUpXl ? 252 : 155 }}
                  rowClassName={(_, index) =>
                    `new-contacts-card-details-row-position-${index}`
                  }
                  onRow={({ label }) =>
                    ({
                      'data-lgg-id': `new-contacts-card-details-row-${label}`,
                    } as HTMLAttributes<HTMLElement>)
                  }
                />
              </DetailsWrapper>
            </CardDataContainer>
          </CardContentContainer>
        ) : (
          <EmptyCardContentContainer $isEmpty>
            <Card.EmptyState
              icon="pieChart"
              title={t('dashboard:cards.newContacts.emptyState.title')}
              subTitle={t('dashboard:cards.newContacts.emptyState.subTitle')}
              iconVariant="large"
              testId="new-contacts-card-empty-state"
            />
          </EmptyCardContentContainer>
        )}
      </Card.Content>
    </Card>
  );
};
